
import React from 'react';
import { BotMessageSquare, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { TopicType } from '@/types/assistant';
import { TOPIC_CONFIG } from '@/contexts/AiAssistantContext';

interface MessageAvatarProps {
  isAi: boolean;
  topic: TopicType;
  isStreaming: boolean;
}

export const MessageAvatar: React.FC<MessageAvatarProps> = React.memo(({ 
  isAi, 
  topic, 
  isStreaming 
}) => {
  const topicConfig = TOPIC_CONFIG[topic];

  const avatarStreamingAnimation = {
    scale: [1, 1.05, 1],
    transition: { 
      duration: 2.5, 
      repeat: Infinity, 
      ease: "easeInOut",
      times: [0, 0.5, 1]
    }
  };

  const breathingAnimation = {
    scale: [1, 1.02, 1],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  if (isAi) {
    return (
      <motion.div 
        className={cn(
          "flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-xl",
          "shadow-lg transition-all duration-300",
          "bg-gradient-to-br from-background/60 to-background/40",
          "backdrop-blur-md border border-white/20",
          isStreaming && [
            "border-blue-400/40 bg-gradient-to-br from-blue-500/10 to-blue-600/5",
            "shadow-blue-400/20 shadow-xl"
          ],
          !isStreaming && topicConfig.bgColor,
          "hover:shadow-xl hover:border-white/30"
        )}
        animate={isStreaming ? avatarStreamingAnimation : breathingAnimation}
      >
        <div className={cn(
          "absolute inset-0 rounded-xl opacity-50",
          "bg-gradient-to-br from-white/5 to-transparent"
        )} />
        
        <BotMessageSquare 
          className={cn(
            "h-4 w-4 relative z-10 transition-colors duration-200",
            isStreaming ? "text-blue-400" : topicConfig.color
          )} 
        />
      </motion.div>
    );
  }

  return (
    <motion.div 
      className={cn(
        "flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-xl",
        "bg-gradient-to-br from-primary/20 to-primary/10",
        "backdrop-blur-sm border border-primary/30 shadow-md",
        "hover:shadow-lg hover:border-primary/40 transition-all duration-200"
      )}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/10 to-transparent opacity-60" />
      
      <User className="h-4 w-4 text-primary relative z-10" />
    </motion.div>
  );
});

MessageAvatar.displayName = "MessageAvatar";
